import { useState } from 'react';
import {
  CodeEditor,
  FlexColumn,
  FlexSpace,
  FormControls,
  LoadingButton,
  TextFieldReadOnly,
} from '@elementary/common-ui/components';
import {
  capitalize,
  pluralizeString,
} from '@elementary/common-ui/utils/common';
import { formatCustomSqlCloudSchedule } from '@elementary/common-ui/utils/customSqlTestConfig';
import { Typography } from '@mui/material';
import SeverityRenderer from 'shared/components/SeverityRenderer';
import useSelectedWarehouseType from 'shared/hooks/useSelectedWarehouseType';
import { QualityDimension, Severity } from 'shared/types';
import { CustomSqlTestConfig } from '../../types';

type Props = {
  testConfig: CustomSqlTestConfig;
  onEdit?: () => void;
  testedTable?: string;
  qualityDimension?: QualityDimension;
};

const toSeverity = (value: string): Severity => {
  const normalized = value.toLowerCase();
  if (normalized === 'warn' || normalized === 'warning') {
    return 'warn';
  }
  return 'error';
};

const toThresholdIf = (threshold?: number | null): string | undefined => {
  if (threshold == null || threshold === 0) {
    return undefined;
  }
  return `> ${threshold}`;
};

const CustomSqlTestConfiguration = ({
  testConfig,
  onEdit,
  testedTable,
  qualityDimension,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const warehouseType = useSelectedWarehouseType();
  const { store_row_sample, sample_row_limit, query_timeout_seconds } =
    testConfig.sql_config;

  return (
    <FormControls.TestAccordion
      summary={
        <FlexSpace sx={{ width: '100%', height: '24px' }}>
          <Typography sx={{ lineHeight: 0 }}>Test Settings</Typography>
          {isExpanded && !!onEdit && (
            <LoadingButton
              sx={{ width: 'fit-content', margin: '0 0 0 auto' }}
              onClick={(e) => {
                onEdit();
                e.stopPropagation();
              }}
            >
              Edit
            </LoadingButton>
          )}
        </FlexSpace>
      }
      expanded={isExpanded}
      onChange={(_, expanded) => setIsExpanded(expanded)}
    >
      <FlexColumn sx={{ gap: 3, p: 3 }}>
        <FlexColumn sx={{ gap: 1 }}>
          <FormControls.FormTitle>Test Parameters</FormControls.FormTitle>
          <CodeEditor
            maxLines={20}
            mode="sql"
            warehouseType={warehouseType}
            value={testConfig.sql}
            readOnly
          />
        </FlexColumn>

        <FormControls.FieldsWrapper>
          <FormControls.Row>
            <FormControls.FormText>Cadence:</FormControls.FormText>
            <TextFieldReadOnly
              value={formatCustomSqlCloudSchedule(testConfig.schedule)}
            />
          </FormControls.Row>
          {store_row_sample != null && (
            <FormControls.Row>
              <FormControls.FormText>Store row sample:</FormControls.FormText>
              <TextFieldReadOnly value={store_row_sample ? 'Yes' : 'No'} />
            </FormControls.Row>
          )}
          {sample_row_limit != null && (
            <FormControls.Row>
              <FormControls.FormText>Sample row limit:</FormControls.FormText>
              <TextFieldReadOnly value={String(sample_row_limit)} />
            </FormControls.Row>
          )}
          {query_timeout_seconds != null && (
            <FormControls.Row>
              <FormControls.FormText>Query timeout:</FormControls.FormText>
              <TextFieldReadOnly
                value={pluralizeString(
                  query_timeout_seconds,
                  'second',
                  'seconds',
                )}
              />
            </FormControls.Row>
          )}
          {qualityDimension && (
            <FormControls.Row>
              <FormControls.FormText>Quality dimension:</FormControls.FormText>
              <TextFieldReadOnly value={capitalize(qualityDimension)} />
            </FormControls.Row>
          )}
          {testedTable && (
            <FormControls.Row>
              <FormControls.FormText>Tested table:</FormControls.FormText>
              <TextFieldReadOnly value={testedTable} />
            </FormControls.Row>
          )}
          <SeverityRenderer
            severity={toSeverity(testConfig.severity)}
            errorIf={toThresholdIf(
              testConfig.sql_config.fail_row_count_threshold,
            )}
            warnIf={toThresholdIf(
              testConfig.sql_config.warn_row_count_threshold,
            )}
          />
        </FormControls.FieldsWrapper>
      </FlexColumn>
    </FormControls.TestAccordion>
  );
};

export default CustomSqlTestConfiguration;
